import type { Node as PMNode } from "@tiptap/pm/model";

/**
 * Node types that carry no content of their own — they only give shape to text.
 *
 * Anything NOT in this set counts as content (image, table, mention, horizontal
 * rule, …), so a node type added to the editor in the future FAILS CLOSED: it
 * reads as "the writer put something here", never as emptiness. Erring that way
 * leaves a stray bullet behind; erring the other way DELETES someone's content.
 */
const STRUCTURAL_NODE_TYPES = new Set([
  "doc",
  "paragraph",
  "text",
  "hardBreak",
  "heading",
  "blockquote",
  "bulletList",
  "orderedList",
  "listItem",
  "codeBlock",
]);

/**
 * The `@` that `RichTextEditor`'s `autoMention` types on mount to open the mention
 * picker (the "+ Aufgabe" button and the `A` key while presenting). It is our
 * prime, not the writer's text — so an entry left holding nothing but this is
 * still empty. Once a person IS picked the doc holds a `mention` NODE, which is
 * not structural, so the entry is correctly kept.
 */
const AUTO_MENTION_PRIME = "@";

/**
 * Did the writer put anything into this editor?
 *
 * Used by the memo ghost-row cleanup (`MemoEntryRow.exitEditor`): an entry opened
 * via "+ Eintrag" / "+ Aufgabe" and abandoned without content is deleted rather
 * than left behind as a placeholder.
 *
 * 🔴 This must be asked of the DOCUMENT, not of the markdown. `md.trim() === ""`
 * — the test this replaces — is fooled by an EMPTY BULLET, which tiptap-markdown
 * serializes to its marker ("-"), so the guard saw content and the ghost survived.
 *
 * 🔴 And it must NOT be `doc.textContent.trim() === ""` alone: an image node has
 * no text content, so an entry holding only a pasted screenshot would be silently
 * deleted. Hence the structural allowlist above.
 */
export function isBlankDoc(doc: PMNode): boolean {
  const text = doc.textContent.trim();
  if (text !== "" && text !== AUTO_MENTION_PRIME) return false;

  let blank = true;
  doc.descendants((node) => {
    if (!blank) return false; // already decided — stop walking
    if (STRUCTURAL_NODE_TYPES.has(node.type.name)) return true;
    blank = false;
    return false;
  });
  return blank;
}
